import { useState } from 'react';

import { UserCreateIn, UserUpdateIn } from '@/api/generated/schemas';
import { useCreateUser, useUpdateUser, type UserChange } from '@/api/queries';
import type { AuthSource, Role, UserAccount } from '@/api/types';
import { fieldErrors, serverFieldErrors } from '@/api/validate';
import { useSession } from '@/session/SessionContext';

type Question = 'source' | 'access';

/** The roles field's note: why it is locked, or what an empty choice means. */
export function roleHint(isCaller: boolean, mayChangeRoles: boolean, roles: Role[], adding: boolean, adminLocked = false): string | null {
  if (adminLocked) return ADMIN_ACCOUNT_REASON;
  if (isCaller) return 'You cannot change your own roles. Another admin must.';
  if (!mayChangeRoles) return 'Changing roles needs the Change roles permission.';
  if (roles.length > 0) return null;
  return adding ? 'Choose at least one role.' : 'No access until a role is given.';
}

/** Why a non-Admin cannot save any change to an account that holds Admin. */
export const ADMIN_ACCOUNT_REASON = 'Only an Admin can change an Admin account.';

/** Why the Admin checkbox is locked for this caller, or null. Only an Admin gives or takes the role. */
export const ADMIN_ONLY_REASON = 'Only an Admin can give or take away the Admin role.';

/** The Source field's hint: why it is locked, or how the account will sign in. */
export function sourceHint(isCaller: boolean, maySwitch: boolean, source: AuthSource): string {
  if (isCaller) return 'You cannot change how your own account signs in.';
  if (!maySwitch) return 'Changing how people sign in needs the Change how users sign in permission.';
  return source === 'entra' ? 'They sign in with Microsoft. Nothing to send them.' : 'They sign in with the password you set.';
}

/** The e-mail field's hint on Edit: why it is locked, or nothing. */
export function emailHint(user: UserAccount, isCaller: boolean, mayEdit: boolean): string | null {
  if (user.entra_linked) return 'Linked to Microsoft: the e-mail no longer changes.';
  if (isCaller) return 'You cannot change your own e-mail.';
  if (!mayEdit) return 'Changing e-mails needs the Edit users permission.';
  return null;
}

/** The Display name's hint: why it cannot be typed, or nothing. Microsoft owns an Entra ID account's name. */
export function nameHint(source: AuthSource, mayRename: boolean): string | null {
  if (source === 'entra') return 'Updated from Microsoft at each sign-in.';
  if (!mayRename) return 'Renaming needs the Edit users permission.';
  return null;
}

/** What an edit sends: the fields the caller may change, the e-mail and the Source only when they change;
 *  never a name for an account that will be Entra ID (Microsoft sets it). */
function editChange(form: {
  user: UserAccount;
  name: string;
  email: string;
  canEditEmail: boolean;
  roles: Role[];
  source: AuthSource;
  password: string;
  canRename: boolean;
  canChangeRole: boolean;
}): UserChange {
  const switching = form.source !== form.user.auth_source;
  const newEmail = form.email.trim().toLowerCase();
  return {
    ...(form.canRename && form.source === 'local' ? { display_name: form.name } : {}),
    ...(form.canEditEmail && newEmail !== form.user.email ? { email: newEmail } : {}),
    ...(form.canChangeRole && !sameRoles(form.roles, form.user.roles) ? { roles: form.roles } : {}),
    ...(switching ? { auth_source: form.source } : {}),
    ...(switching && form.source === 'local' ? { password: form.password } : {}),
  };
}

function sameRoles(a: Role[], b: Role[]): boolean {
  return a.length === b.length && a.every((role) => b.includes(role));
}

function holdsAdmin(caller: { roles: Role[] } | null): boolean {
  return caller !== null && caller.roles.includes('admin');
}

/** Adding picks the Source freely; editing needs the permission, and never on one's own account. */
function sourceRights(user: UserAccount | undefined, isCaller: boolean, maySwitch: boolean) {
  return {
    canChangeSource: !user || (maySwitch && !isCaller),
    ownSource: isCaller && user !== undefined,
    mayPickSource: maySwitch || !user,
  };
}

/** What the caller may change in this dialog. */
function useDialogRights(user: UserAccount | undefined, isCaller: boolean) {
  const { can, user: caller } = useSession();
  const mayChangeRoles = can('user.role.change');
  const maySwitch = can('user.source.change');
  const accountLocked = user !== undefined && user.roles.includes('admin') && !holdsAdmin(caller);
  return {
    ...sourceRights(user, isCaller, maySwitch),
    callerIsAdmin: holdsAdmin(caller),
    mayChangeRoles,
    canRename: !user || can('user.update'),
    // An Admin account answers every write from a non-Admin with 403: lock it up front.
    accountLocked,
    canChangeRole: mayChangeRoles && !isCaller && !accountLocked,
    emailHint: user ? emailHint(user, isCaller, !user || can('user.update')) : null,
  };
}

/** The form's starting values: the account's own, or a new Entra ID user. */
function initialForm(user: UserAccount | undefined) {
  return user
    ? { email: user.email, name: user.display_name, roles: user.roles, source: user.auth_source }
    : { email: '', name: '', roles: ['user'] as Role[], source: 'entra' as AuthSource };
}

/**
 * Add (no `user`) or edit an account. The name follows `user.update`, the role
 * `user.role.change` (any set of roles), the Source `user.source.change`; only what the caller may
 * change is sent. A switch to Entra ID asks first: it removes the password.
 */
export function useUserDialog(user: UserAccount | undefined, isCaller: boolean, onClose: () => void) {
  const create = useCreateUser();
  const update = useUpdateUser();
  const rights = useDialogRights(user, isCaller);
  const initial = initialForm(user);
  const [email, setEmail] = useState(initial.email);
  const [name, setName] = useState(initial.name);
  const [roles, setRoles] = useState<Role[]>(initial.roles);
  const [source, setSource] = useState<AuthSource>(initial.source);
  const [password, setPassword] = useState('');
  // What the person is asked before saving, and what they already agreed to.
  const [asking, setAsking] = useState<Question | null>(null);
  const [agreed, setAgreed] = useState<Question[]>([]);
  const switching = user !== undefined && source !== user.auth_source;
  const clearsAccess = user !== undefined && user.roles.length > 0 && roles.length === 0 && rights.canChangeRole;

  const local = source === 'local' ? { display_name: name, password } : {};
  const created = { email, roles, auth_source: source, ...local };
  const change = user ? editChange({ user, name, email, canEditEmail: rights.emailHint === null, roles, source, password, ...rights }) : null;
  // The API's own rules on what would be sent; the dialog's locks and the role note stay its own.
  const invalid = Object.keys(change ? fieldErrors(UserUpdateIn, change) : fieldErrors(UserCreateIn, created)).length > 0;

  function submit(done: Question[] = agreed) {
    if (!user || !change) {
      create.mutate(created, { onSuccess: onClose });
      return;
    }
    const ask: Question[] = [...(switching && source === 'entra' ? (['source'] as const) : []), ...(clearsAccess ? (['access'] as const) : [])];
    const next = ask.find((q) => !done.includes(q));
    if (next) {
      setAsking(next);
      return;
    }
    update.mutate({ id: user.id, change }, { onSuccess: onClose, onError: cancelAsk });
  }

  function cancelAsk() {
    setAsking(null);
    setAgreed([]);
  }

  /** The person said yes to the open question; ask the next one or save. */
  function agree() {
    if (asking === null) return;
    const done = [...agreed, asking];
    setAgreed(done);
    setAsking(null);
    submit(done);
  }

  return {
    mutation: user ? update : create,
    submit: () => submit(),
    email,
    setEmail,
    name,
    setName,
    roles,
    toggleRole: (role: Role, on: boolean) => setRoles((held) => (on ? [...held, role] : held.filter((r) => r !== role))),
    source,
    setSource,
    password,
    setPassword,
    asking,
    agree,
    cancelAsk,
    clearsAccess,
    switching,
    server: serverFieldErrors((user ? update : create).error),
    ...dialogView({ user, isCaller, source, roles, switching, invalid }, rights),
  };
}

/** What the form shows for the current values: hints, locks and which fields apply. */
function dialogView(
  form: { user: UserAccount | undefined; isCaller: boolean; source: AuthSource; roles: Role[]; switching: boolean; invalid: boolean },
  rights: ReturnType<typeof useDialogRights>,
) {
  const { user, source } = form;
  return {
    adminLocked: !rights.callerIsAdmin,
    accountLocked: rights.accountLocked,
    canRename: rights.canRename,
    emailHint: rights.emailHint,
    // Add asks a name only of a Local account; Edit always shows it, read-only while Entra ID.
    showName: user !== undefined || source === 'local',
    nameHint: nameHint(source, rights.canRename),
    canChangeRole: rights.canChangeRole,
    canChangeSource: rights.canChangeSource,
    needsPassword: source === 'local' && (!user || form.switching),
    hint: roleHint(form.isCaller, rights.mayChangeRoles, form.roles, !user, rights.accountLocked),
    // Add needs a role (the API refuses none), and nobody saves into an Admin account without being one.
    incomplete: (!user && form.roles.length === 0) || rights.accountLocked || form.invalid,
    // On Add the subtitle already says how they sign in; only a lock needs explaining.
    sourceHint: user || !rights.mayPickSource ? sourceHint(rights.ownSource, rights.mayPickSource, source) : null,
  };
}
